/**
 * Editorial Authoring V2 — page shell (BUILD 3: HERO → Story handoff).
 * Route: /admin/editorial-v2/materials/:materialId
 *
 * Deliberately independent from the legacy authoring page
 * (frontend/src/pages/admin/Editorial.jsx), which is untouched by this
 * BUILD. Reads the same existing Material/EditorialBlock/Volume/Media
 * data via the same existing endpoints, but renders it only through
 * the new V2 components — no import from the old authoring UI.
 *
 * Destination context is resolved from the confirmed contract only:
 * GET /admin/editorial/volumes + material.volume_id, matched here and
 * passed down as `volume` — no guessed field names on `material`.
 *
 * One GET /admin/media is made here at page load (not one per gallery
 * card) so cards can resolve a saved content.media_ids entry into a
 * real thumbnail. After a Save in the workspace, the freshly saved/
 * uploaded media is merged into mediaRecords directly from what the
 * workspace already resolved — no second GET /admin/media round-trip
 * on every save.
 *
 * HERO → EDITORIAL_STORY HANDOFF (BUILD 3): this page owns the full
 * ordered blocks[] for the material, so target selection for HERO's
 * "Продолжить в редакционной истории" lives here — EditorialHeroV2
 * itself has no visibility into sibling blocks. handleContinueInStory
 * finds the first EDITORIAL_STORY block whose sort_order is greater
 * than the HERO block's, and opens it directly (switching openBlockId
 * from the HERO block to the target — EditorialSpreadWorkspaceV2 then
 * unmounts EditorialHeroV2 and mounts EditorialStoryV2 in the same
 * position, since they're different component types). If no such
 * block exists, a toast explains it and the leftover text is left
 * untouched on the HERO block — nothing is lost or auto-created.
 *
 * The leftover text used for the handoff is EditorialHeroV2's current
 * local `leftoverText` state, passed explicitly as the handler's
 * second argument — not read from heroBlock.content.extra directly.
 * If the author just ran "Распознать текст" but hasn't pressed HERO's
 * own "Сохранить изменения" yet, the saved
 * content.extra.hero_leftover_text would still be the old value; the
 * local state is always current. heroBlock.content.extra.hero_leftover_text
 * is kept only as a fallback for calls that don't supply it.
 *
 * The handoff itself is transient page state only
 * ({ sourceBlockId, targetBlockId, sourceText }) — never written to
 * the backend. EditorialStoryV2 receives it as `handoffSource` only
 * when the currently open block is the handoff target, and decides
 * locally whether to auto-prefill an empty draft or show a compact
 * "already has text" choice (never a browser confirm/alert). The
 * transient handoff state is cleared here, in handleSaved, once the
 * target block is actually saved — not merely opened or closed — so a
 * Close-without-Save leaves it available to reapply on a later visit,
 * matching "no DB mutation before explicit Story Save".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";
import { EditorialMaterialGalleryV2 } from "@/components/editorial-v2/EditorialMaterialGalleryV2";
import { EditorialSpreadWorkspaceV2 } from "@/components/editorial-v2/EditorialSpreadWorkspaceV2";
import { EditorialWholeMaterialPublishV2 } from "@/components/editorial-v2/EditorialWholeMaterialPublishV2";
import { EDITORIAL_MASTER_SHOWCASE_ID } from "../../lib/editorialAuthorStartV1.mjs";
import { editorialShowcaseDisplayMode, validEditorialWorkspace } from "../../lib/editorialShowcaseEntryV1.mjs";

const ED = "/admin/editorial";
const MEDIA_LIST = "/admin/media";

// Merges freshly known {id, url} pairs (from a just-completed Save) into
// the existing media records list, without a re-fetch. Synthetic entries
// carry both `url` and `file_url` since we don't know which field the
// shared findMediaUrl/mediaRecordUrl helpers read internally.
function mergeMediaRecords(records, updates = []) {
  if (!updates.length) return records;
  const byId = new Map(records.map((r) => [r.id, r]));
  updates.forEach((u) => {
    if (!u?.id || !u?.url) return;
    byId.set(u.id, { ...(byId.get(u.id) || {}), id: u.id, url: u.url, file_url: u.url });
  });
  return Array.from(byId.values());
}

export default function EditorialV2() {
  const { materialId } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const requestedBlockId = location.state?.openBlockId;
  const query = new URLSearchParams(location.search || "");
  const returnSpace = validEditorialWorkspace(location.state?.returnSpace)
    || validEditorialWorkspace(query.get("from_workspace"));
  const referenceView = editorialShowcaseDisplayMode({
    materialId,
    sourceSpace: returnSpace,
    showcase: query.get("showcase") === "1",
  });

  const [material, setMaterial] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [volume, setVolume] = useState(null);
  const [mediaRecords, setMediaRecords] = useState([]);
  const [openBlockId, setOpenBlockId] = useState(null);
  const [newStoryMode, setNewStoryMode] = useState(false);
  const [handoff, setHandoff] = useState(null);
  const [masterChoicesOpen, setMasterChoicesOpen] = useState(false);
  const [masterTypes, setMasterTypes] = useState([]);
  const [addingSpread, setAddingSpread] = useState(false);
  const addingSpreadLock = useRef(false);

  const load = useCallback(async (isCurrent) => {
    try {
      const [m, bs, vols, media] = await Promise.all([
        api.get(`${ED}/materials/${materialId}`),
        api.get(`${ED}/materials/${materialId}/blocks`),
        api.get(`${ED}/volumes`),
        api.get(MEDIA_LIST).catch(() => ({ data: [] })),
      ]);
      if (!isCurrent()) return;
      setMaterial(m.data);
      // The showcase is read-only: loading it must never repair sample records.
      const loadedBlocks = bs.data || [];
      setBlocks(loadedBlocks);
      // Open the new author draft directly inside the existing MASTER workspace.
      if (requestedBlockId && loadedBlocks.some((b) => b.id === requestedBlockId)) {
        setOpenBlockId(requestedBlockId);
      }
      const matchedVolume = (vols.data || []).find((v) => v.id === m.data.volume_id) || null;
      setVolume(matchedVolume);
      const records = Array.isArray(media.data)
        ? media.data
        : (media.data?.items || media.data?.results || []);
      setMediaRecords(records);
    } catch (e) {
      if (isCurrent()) toast.error(formatApiError(e));
    }
  }, [materialId, requestedBlockId]);

  useEffect(() => {
    let cancelled = false;
    setOpenBlockId(null);
    setNewStoryMode(false);
    setHandoff(null);
    setMasterChoicesOpen(false);
    load(() => !cancelled);
    return () => { cancelled = true; };
  }, [load]);


  useEffect(() => {
    const onDestinationChanged = (event) => {
      const detail = event.detail || {};
      if (detail.materialId !== materialId) return;
      if (detail.material) setMaterial(detail.material);
      if (detail.volume) setVolume(detail.volume);
    };
    const onSectionChanged = (event) => {
      const detail = event.detail || {};
      if (detail.materialId === materialId && detail.material) {
        setMaterial(detail.material);
      }
    };
    window.addEventListener("editorial-v2-destination-changed", onDestinationChanged);
    window.addEventListener("editorial-v2-section-changed", onSectionChanged);
    return () => {
      window.removeEventListener("editorial-v2-destination-changed", onDestinationChanged);
      window.removeEventListener("editorial-v2-section-changed", onSectionChanged);
    };
  }, [materialId]);

  const openBlock = blocks.find((b) => b.id === openBlockId) || null;

  // Open the approved MASTER editor directly. Merely opening a card must
  // never create an author material or spread, or navigate away from the ten
  // original workspaces. Existing publication safety guards stay unchanged.
  const handleOpenBlock = (blockId) => {
    if (referenceView.referenceOnly) {
      toast("Это образец оформления. Для публикаций в выбранном пространстве сначала подключим его редакционную структуру.");
      return;
    }
    if (material?.id !== materialId) return;
    if (!blocks.some((b) => b.id === blockId)) return;
    setNewStoryMode(false);
    setOpenBlockId(blockId);
  };

  const startNewStory = (blockId) => {
    if (referenceView.referenceOnly) return;
    if (material?.id !== EDITORIAL_MASTER_SHOWCASE_ID) return;
    const sample = blocks.find(b => b.id === blockId && b.master_type === "EDITORIAL_STORY");
    if (!sample) return;
    setNewStoryMode(true);
    setOpenBlockId(blockId);
  };

  // Existing author card = one editorial_material with many original MASTER spreads.
  // No second editor, QA fixture writes, or new material on ordinary card click.
  const toggleMasterChoices = async () => {
    if (masterChoicesOpen) { setMasterChoicesOpen(false); return; }
    if (material?.id !== materialId || material.id === EDITORIAL_MASTER_SHOWCASE_ID
        || material.status === "archived") return;
    try {
      const { data } = await api.get(`${ED}/master-types`);
      const options = Array.isArray(data) ? data.filter((t) => t?.key && t?.label) : [];
      if (!options.length) throw new Error("Список MASTER недоступен.");
      setMasterTypes(options);
      setMasterChoicesOpen(true);
    } catch (error) { toast.error(formatApiError(error)); }
  };

  const addSpreadToCard = async (masterType) => {
    if (addingSpreadLock.current || material?.id !== materialId
        || material.id === EDITORIAL_MASTER_SHOWCASE_ID || material.status === "archived"
        || !masterTypes.some((t) => t.key === masterType)) return;
    addingSpreadLock.current = true;
    setAddingSpread(true);
    try {
      const { data: created } = await api.post(
        `${ED}/materials/${encodeURIComponent(material.id)}/blocks`,
        { material_id: material.id, master_type: masterType, sort_order: 0,
          content: {}, settings: {}, is_visible: true },
      );
      if (!created?.id || created.material_id !== material.id || created.master_type !== masterType
          || blocks.some((b) => b.id === created.id)) {
        throw new Error("Не удалось добавить разворот в эту карточку.");
      }
      setBlocks((items) => [...items, created]);
      setOpenBlockId(created.id);
      setNewStoryMode(false);
      setMasterChoicesOpen(false);
    } catch (error) { toast.error(formatApiError(error)); }
    finally { addingSpreadLock.current = false; setAddingSpread(false); }
  };

  const handleAuthorCreated = (created) => {
    // The new draft was persisted by an explicit Save, not by opening a MASTER.
    // The ordinary route opens the same existing Story editor on its own block.
    setNewStoryMode(false);
    setOpenBlockId(null);
    nav(`/admin/editorial-v2/materials/${encodeURIComponent(created.material.id)}`, {
      state: { openBlockId: created.block.id, returnSpace: location.state?.returnSpace },
    });
  };

  const handleSaved = (updatedBlock, savedMediaItems) => {
    setBlocks((bs) => bs.map((b) => (b.id === updatedBlock.id ? updatedBlock : b)));
    setMediaRecords((records) => mergeMediaRecords(records, savedMediaItems));
    // Handoff is consumed only after the target block was really saved —
    // not merely opened, and not on Close without Save.
    setHandoff((h) => (h && h.targetBlockId === updatedBlock.id ? null : h));
  };

  // HERO's "Продолжить в редакционной истории" — see file header for
  // the full contract. heroBlock is passed by EditorialHeroV2 via
  // onContinueInStory so this always reads the current block from this
  // page's own blocks[] rather than trusting a possibly-stale prop.
  const handleContinueInStory = (heroBlock, currentLeftover) => {
    const leftover = String(
      currentLeftover ?? heroBlock?.content?.extra?.hero_leftover_text ?? ""
    ).trim();
    if (!leftover) return;

    const target = blocks
      .filter((b) => b.master_type === "EDITORIAL_STORY" && b.sort_order > heroBlock.sort_order)
      .sort((a, b) => a.sort_order - b.sort_order)[0] || null;

    if (!target) {
      toast("После обложки нет разворота «Редакционная история».");
      return;
    }

    setHandoff({ sourceBlockId: heroBlock.id, targetBlockId: target.id, sourceText: leftover });
    setOpenBlockId(target.id);
  };

  if (material?.id !== materialId) return <div className="min-h-screen bg-cream" data-testid="editorial-v2-loading" />;

  return (
    <div className="min-h-screen bg-cream" data-testid="editorial-v2-material">
      <header className="border-b border-espresso/10 bg-white/60 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-5 py-4">
          <button onClick={() => nav(referenceView.backPath)} className="p-2 -ml-2 rounded-full hover:bg-sand/50"
            data-testid="editorial-v2-back">
            <ChevronLeft className="h-5 w-5 text-espresso" />
          </button>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.28em] text-espresso/50">Anokina Studio · V2</div>
            <h1 className="font-serif text-2xl text-espresso">{material.id === EDITORIAL_MASTER_SHOWCASE_ID ? "Редактор — образцы оформления" : material.title}</h1>
          </div>
          {material.id !== EDITORIAL_MASTER_SHOWCASE_ID && (
            <EditorialWholeMaterialPublishV2
              material={material}
              volume={volume}
              editorOpen={Boolean(openBlock)}
              onPublished={(updatedMaterial) => setMaterial(updatedMaterial)}
            />
          )}
          {volume?.slug === "open-library-author-intro" && (
            <Link
              to={`/admin/editorial-v2/materials/${encodeURIComponent(materialId)}/preview-in-library`}
              target="_blank" rel="noopener noreferrer"
              className="rounded-xl border border-espresso/25 bg-white px-4 py-3 text-xs text-espresso hover:bg-sand/30"
              data-testid="editorial-open-library-draft-preview">
              Посмотреть на странице библиотеки
            </Link>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 py-8">
        {referenceView.referenceOnly && (
          <div className="mb-5 rounded-2xl border border-[#D7C5AD] bg-[#F4EEE4] px-5 py-4" data-testid="editorial-reference-only-notice">
            <p className="font-serif text-lg text-espresso">Образцы оформления для «{({
              almond: "Том I «Миндаль»", medovik: "Медовик как бизнес",
              bento: "Бенто-торты от А до Я", lineart: "Контурные рисунки на бенто",
              gingerbread: "Пряничная мастерская", "media-studio": "Медиа-студия",
            })[returnSpace] || "выбранного пространства"}»</p>
            <p className="mt-1 text-sm text-espresso/70">
              Вы смотрите общий набор макетов. Редактирование образцов и создание публикации
              для этого пространства пока недоступны, чтобы материал не сохранился в другой библиотеке.
            </p>
          </div>
        )}
        <EditorialMaterialGalleryV2 blocks={blocks} mediaRecords={mediaRecords} onOpenBlock={handleOpenBlock} />
        {material?.id !== EDITORIAL_MASTER_SHOWCASE_ID && material?.status !== "archived" && (
          <div className="mt-5" data-testid="editorial-v2-add-to-card">
            <button type="button" onClick={toggleMasterChoices} disabled={addingSpread}
              className="rounded-xl border border-espresso/25 bg-white px-4 py-3 text-sm text-espresso disabled:opacity-50"
              data-testid="editorial-v2-add-spread">
              {masterChoicesOpen ? "Скрыть выбор" : "Добавить разворот"}
            </button>
            {masterChoicesOpen && (
              <div className="mt-3 flex flex-wrap gap-2 rounded-2xl border border-espresso/10 bg-white/70 p-3"
                data-testid="editorial-v2-master-choices">
                {masterTypes.map((item) => (
                  <button key={item.key} type="button" disabled={addingSpread}
                    onClick={() => addSpreadToCard(item.key)}
                    className="rounded-xl bg-cream px-3 py-2 text-sm text-espresso disabled:opacity-50"
                    data-testid={`editorial-v2-add-master-${item.key}`}>{item.label}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {material?.id === EDITORIAL_MASTER_SHOWCASE_ID && !referenceView.referenceOnly && (
          <button type="button" onClick={() => startNewStory(blocks.find(b => b.master_type === "EDITORIAL_STORY")?.id)}
            className="mt-5 rounded-xl border border-espresso/25 bg-white px-4 py-3 text-sm text-espresso"
            data-testid="editorial-v1-new-story">Создать новую редакционную историю</button>
        )}
      </div>

      {openBlock && (
        <EditorialSpreadWorkspaceV2
          key={`${openBlock.id}:${newStoryMode ? "new" : "existing"}`}
          block={openBlock}
          sourceMaterial={material}
          newStoryMode={newStoryMode && material.id === EDITORIAL_MASTER_SHOWCASE_ID && openBlock.master_type === "EDITORIAL_STORY"}
          onStartNewStory={() => setNewStoryMode(true)}
          onAuthorCreated={handleAuthorCreated}
          volume={volume}
          onClose={() => { setOpenBlockId(null); setNewStoryMode(false); }}
          onSaved={handleSaved}
          onContinueInStory={handleContinueInStory}
          handoffSource={handoff && handoff.targetBlockId === openBlock.id ? handoff.sourceText : null}
        />
      )}
    </div>
  );
}
